import { useMemo } from "react";

export interface PasswordRule {
  id: string;
  label: string;
  test: (pw: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  { id: "length", label: "Au moins 8 caractères", test: (pw) => pw.length >= 8 },
  { id: "uppercase", label: "Une lettre majuscule", test: (pw) => /[A-Z]/.test(pw) },
  { id: "lowercase", label: "Une lettre minuscule", test: (pw) => /[a-z]/.test(pw) },
  { id: "number", label: "Un chiffre", test: (pw) => /[0-9]/.test(pw) },
  {
    id: "special",
    label: "Un caractère spécial (!@#$%...)",
    test: (pw) => /[!@#$%^&*(),.?":{}|<>\-_]/.test(pw),
  },
];

export interface StrengthResult {
  score: number; // 0 to 5
  maxScore: number;
  label: "Faible" | "Moyen" | "Fort";
  color: string;
  ruleStatus: Record<string, boolean>;
  isValid: boolean;
}

export function usePasswordStrength(password: string): StrengthResult {
  return useMemo(() => {
    if (!password) {
      return {
        score: 0,
        maxScore: PASSWORD_RULES.length,
        label: "Faible",
        color: "text-red-500",
        ruleStatus: {
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
          special: false,
        },
        isValid: false,
      };
    }

    const ruleStatus: Record<string, boolean> = {};
    let score = 0;

    PASSWORD_RULES.forEach((rule) => {
      const passed = rule.test(password);
      ruleStatus[rule.id] = passed;
      if (passed) {
        score += 1;
      }
    });

    let label: "Faible" | "Moyen" | "Fort" = "Faible";
    let color = "text-red-500";

    if (score >= 5) {
      label = "Fort";
      color = "text-emerald-500";
    } else if (score >= 3) {
      label = "Moyen";
      color = "text-amber-500";
    }

    return {
      score,
      maxScore: PASSWORD_RULES.length,
      label,
      color,
      ruleStatus,
      isValid: score === PASSWORD_RULES.length,
    };
  }, [password]);
}
